/**
 * tutorPrescriptionAdapter — « Travail entre les lives » : les élèves du live, leur présence, la
 * proposition du champion et ce qui part au serveur, à la forme que lit `<TutorPrescriptionPage>`.
 *
 * Port littéral de `tutor/study-missions/StudyMissionPrescriptionScreen.js` et de ses deux
 * fonctions pures, `draftToTodoShape.js` et `buildRegeneratePayload.js` : chaque fonction cite
 * le symbole qu'elle reprend.
 */
import { type UIActivityGenerationDraft } from "../../../ui/types"
import {
    type UITutorPrescriptionActivity,
    type UITutorPrescriptionDialog,
    type UITutorPrescriptionDraft,
    type UITutorPrescriptionPresence,
} from "../../../ui/types/tutorPrescription"
import { toUIActivityStatus } from "../../parent/Activities/activityAdapter"
import {
    DEFAULT_SCHOOL_SUBJECT_ID,
    lastFinishedActivitySubjectId,
} from "../../parent/Activities/activityGenerationAdapter"
import { toUISubject, type ApiSchoolSubject } from "../../parent/shared/schoolSubjectAdapter"

/** Un élève passé en paramètre de route par la sortie du live (`LiveModeFooter`). */
export type ApiPrescriptionRouteStudent = {
    student_id?: string | number
    display_name?: string | null
    firstname?: string | null
    avatar?: string | null
    grade?: string | null
}

/** Un participant d'une séance du compte : `users` côté admin, `students` côté tuteur. */
export type ApiPrescriptionSessionUser = {
    id?: string | number
    display_name?: string | null
    firstname?: string | null
    avatar?: string | null
    is_tutor?: boolean | null
    grade?: { name?: string | null } | null
    profile?: { class?: string | null } | null
}

export type ApiPrescriptionLiveSession = {
    id?: string | number
    users?: ApiPrescriptionSessionUser[] | null
    students?: ApiPrescriptionSessionUser[] | null
}

/** L'occurrence lue pour la présence (`getLiveOccurrence`) : ses comptes rendus. */
export type ApiPrescriptionOccurrence = {
    live_reports?: Array<{ statistics?: unknown; student?: { id?: string | number } | null } | null> | null
}

type ApiDraftMaterial = {
    title?: string | null
    class?: string | null
    school_subject_id?: string | number | null
    estimated_duration?: number | null
    chapter_model?: { id?: string | number; name?: string | null } | null
}

/** Une proposition du champion : un support, pas encore une activité (pas d'`id`). */
export type ApiDraftActivity = {
    material_id?: string | number | null
    type?: string | null
    title?: string | null
    school_subject_id?: string | number | null
    estimated_duration_minutes?: number | null
    with_prerequisite_quiz?: boolean | null
    has_prerequisite_quiz?: boolean | null
    student_chapter?: { chapter?: { name?: string | null } | null } | null
    source?: { skills_part_position?: number | null } | null
    material?: ApiDraftMaterial | null
}

/** Le brouillon du champion pour un élève (`state.data.studyMissions`). */
export type ApiStudyMissionDraft = {
    live_occurrence_id?: string | number
    student_id?: string | number
    subject_name?: string | null
    nonce?: string | null
    activities?: ApiDraftActivity[] | null
    activity_ids?: unknown[] | null
    /** Posé par le réducteur quand le masteur a retiré la dernière proposition. */
    emptiedByTutor?: boolean
    /** Le cadrage du champion : le chapitre, la matière et la classe qu'il a retenus. */
    scope?: {
        chapter_id?: string | number | null
        school_subject_id?: string | number | null
        class?: string | null
    } | null
}

export type ApiStudyMissionPrescription = { activity_ids?: unknown[]; activities_count?: number }

/** Une activité déjà à faire, lue sur l'élève du compte (`getStudent` → `todo_activities`). */
export type ApiPrescriptionTodoActivity = {
    id?: string | number
    title?: string | null
    status?: string | null
    estimated_duration?: number | null
    school_subject_id?: string | number | null
    with_prerequisite_quiz?: boolean | null
    has_prerequisite_quiz?: boolean | null
    material?: { title?: string | null } | null
    student_chapter?: { chapter?: { name?: string | null } | null } | null
}

/** Un élève du compte du masteur (`currentUser.students`). */
export type ApiPrescriptionTutorStudent = {
    id?: string | number
    has_study_program?: boolean | null
    profile?: { class?: string | null } | null
    activities?: Array<{ school_subject_id?: string | number | null; finished_at?: string | null } | null> | null
    todo_activities?: ApiPrescriptionTodoActivity[] | null
}

/** Un élève du live, avant sa présence : ce que l'écran sait de lui tout de suite. */
export type PrescriptionRosterEntry = {
    studentId: string | number
    /** `display_name`, puis le prénom, puis « L'élève » — le repli du legacy. */
    name: string
    avatarUrl?: string
    className?: string
}

const NO_ROSTER: PrescriptionRosterEntry[] = []

/**
 * Les élèves du live, dans son ordre — `students` du legacy : ceux des paramètres de route, sinon
 * ceux de la séance du compte (rechargement, lien profond, pile restaurée après la sortie du live).
 * La séance du tuteur expose `students` ; `users` n'existe que côté admin.
 */
export function prescriptionRoster(
    routeStudents: ApiPrescriptionRouteStudent[] | null | undefined,
    liveSession: ApiPrescriptionLiveSession | null | undefined
): PrescriptionRosterEntry[] {
    const fromParams = Array.isArray(routeStudents) && routeStudents.length > 0 ? routeStudents : null
    const base: ApiPrescriptionRouteStudent[] =
        fromParams ??
        (liveSession?.users?.filter((u) => !u?.is_tutor) ?? liveSession?.students ?? []).map((u) => ({
            student_id: u?.id,
            display_name: u?.display_name,
            firstname: u?.firstname,
            avatar: u?.avatar,
            grade: u?.grade?.name ?? u?.profile?.class ?? null,
        }))
    const roster = base
        .map((s): PrescriptionRosterEntry | null =>
            s?.student_id == null
                ? null
                : {
                      studentId: s.student_id,
                      name: s.display_name ?? s.firstname ?? "L'élève",
                      avatarUrl: s.avatar ?? undefined,
                      className: s.grade ?? undefined,
                  }
        )
        .filter((s): s is PrescriptionRosterEntry => s != null)
    return roster.length > 0 ? roster : NO_ROSTER
}

/**
 * Les élèves réellement connectés : ceux dont le compte rendu a des statistiques. On ne génère
 * jamais pour un absent. Les ids en chaîne : la route et le compte ne les typent pas pareil.
 */
export function connectedStudentIds(occurrence: ApiPrescriptionOccurrence | null | undefined): Set<string> {
    const ids = (occurrence?.live_reports ?? [])
        .filter((report) => !!report?.statistics)
        .map((report) => report?.student?.id)
        .filter((id): id is string | number => id != null)
    return new Set(ids.map(String))
}

/** La présence d'un élève : `pending` tant que la lecture n'a pas répondu. */
export function presenceOf(
    connected: ReadonlySet<string> | null,
    studentId: string | number
): UITutorPrescriptionPresence {
    if (connected == null) return "pending"
    return connected.has(String(studentId)) ? "present" : "absent"
}

/**
 * `draftToTodoShape.js` — une proposition du champion (un support sans activité derrière) à la forme
 * d'une activité, pour qu'elle se lise comme une ligne de « Déjà à faire ».
 */
export function draftToTodoShape(activity: ApiDraftActivity | null | undefined) {
    const material = activity?.material ?? null
    return {
        ...activity,
        school_subject_id: activity?.school_subject_id ?? material?.school_subject_id ?? null,
        student_chapter: material?.chapter_model ? { chapter: material.chapter_model } : activity?.student_chapter,
        estimated_duration: activity?.estimated_duration_minutes ?? material?.estimated_duration ?? null,
        duration: null,
    }
}

/** `activityKey` du legacy : la clé stable d'une proposition (elle n'a pas d'id avant l'envoi). */
export function proposalKey(activity: ApiDraftActivity | null | undefined, index: number): string {
    return `${activity?.material_id ?? "nomat"}-${activity?.type ?? "x"}-${index}`
}

/** `hasPrerequisiteQuiz` du legacy : la marque « Quizz ». */
function withQuiz(activity: { with_prerequisite_quiz?: boolean | null; has_prerequisite_quiz?: boolean | null }) {
    return !!(activity?.with_prerequisite_quiz || activity?.has_prerequisite_quiz)
}

/** Une ligne de « Déjà à faire » ou de « À ajouter » — ce que lisait `RealizationsListItem`. */
function toPrescriptionActivity(
    raw: ApiPrescriptionTodoActivity,
    id: string | number,
    subjectsById: Record<string, ApiSchoolSubject>
): UITutorPrescriptionActivity {
    const chapterName = raw.student_chapter?.chapter?.name ?? undefined
    return {
        id,
        title: raw.material?.title ?? raw.title ?? "",
        subject: toUISubject(subjectsById[String(raw.school_subject_id ?? "")]),
        chapterName: chapterName || undefined,
        status: toUIActivityStatus(raw.status ?? "todo"),
        estimatedMinutes: raw.estimated_duration ?? 0,
        withQuiz: withQuiz(raw),
    }
}

export function toUIExistingActivities(
    todos: ApiPrescriptionTodoActivity[] | null | undefined,
    subjectsById: Record<string, ApiSchoolSubject>
): UITutorPrescriptionActivity[] {
    return (todos ?? [])
        .filter((todo): todo is ApiPrescriptionTodoActivity & { id: string | number } => todo?.id != null)
        .map((todo) => toPrescriptionActivity(todo, todo.id, subjectsById))
}

/**
 * La proposition du champion pour un élève présent : en préparation, en échec (« Réessayer »), ou la
 * liste prête. Un brouillon qui arrive l'emporte sur un échec d'avant, comme le réducteur.
 */
export function toUIPrescriptionDraft(
    draft: ApiStudyMissionDraft | null | undefined,
    failed: boolean,
    subjectsById: Record<string, ApiSchoolSubject>
): UITutorPrescriptionDraft {
    if (draft == null) return failed ? { state: "failed" } : { state: "loading" }
    const proposals = (draft.activities ?? []).map((activity, index) =>
        toPrescriptionActivity(
            draftToTodoShape(activity) as ApiPrescriptionTodoActivity,
            proposalKey(activity, index),
            subjectsById
        )
    )
    return { state: "ready", proposals, emptiedByTutor: draft.emptiedByTutor === true }
}

/**
 * Combien d'exercices sont partis, pour le résumé de la carte repliée — la formule du legacy
 * (`proposals.length || draft?.activity_ids?.length || 1`).
 */
export function prescribedNewCount(draft: ApiStudyMissionDraft | null | undefined): number {
    return (draft?.activities?.length ?? 0) || draft?.activity_ids?.length || 1
}

/**
 * Le corps de « Donner à faire » (`handleConfirm`) : la liste « À ajouter » et rien d'autre. Le
 * `material` hydraté est retiré (`sanitizeActivitiesForPrescribe`) : le serveur le relit.
 */
export function prescribeBody(draft: ApiStudyMissionDraft): Record<string, unknown> {
    return {
        live_occurrence_id: draft.live_occurrence_id,
        student_id: draft.student_id,
        subject_name: draft.subject_name,
        nonce: draft.nonce,
        activities: (draft.activities ?? []).map((activity) => {
            const rest = { ...activity }
            delete rest.material
            return rest
        }),
    }
}

/**
 * `collectExcludedSkillParts` : les sous-compétences déjà prises par la séquence, pour que
 * « Ajouter un exercice » en tourne une autre.
 */
function excludedSkillParts(activities: ApiDraftActivity[] | null | undefined): number[] {
    return Array.from(
        new Set(
            (activities ?? [])
                .map((a) => a?.source?.skills_part_position)
                .filter((p): p is number => p === 1 || p === 2 || p === 3)
        )
    )
}

/**
 * Le corps de « Ajouter un exercice » (`handleAddActivity`) : sur le chapitre du brouillon (son
 * cadrage, pas sa première ligne, que le masteur a pu retirer), une autre sous-compétence.
 */
export function generateAdditionalBody(
    occurrenceId: string | number,
    studentId: string | number,
    draft: ApiStudyMissionDraft | null | undefined
): Record<string, unknown> {
    const excluded = excludedSkillParts(draft?.activities)
    const chapterId = draft?.scope?.chapter_id ?? null
    return {
        live_occurrence_id: occurrenceId,
        student_id: studentId,
        ...(excluded.length > 0 ? { excluded_skill_parts: excluded } : {}),
        ...(chapterId ? { chapter_id: chapterId } : {}),
    }
}

/** Le cadrage d'ouverture du formulaire de « Recommencer ». */
export type RegenerateScope = {
    className: string | null
    subjectId: string | null
    chapterId: string | null
    /** L'élève suit un programme de révision : le formulaire offre son mode (éteint à l'ouverture). */
    hasStudyProgram: boolean
}

/**
 * `RegenerateModal.js` + l'ouverture de `GenerateActivityModal` : le chapitre retenu par le champion (le
 * cadrage du brouillon, pas sa première ligne), la classe de l'élève, et la matière du champion — sauf
 * pour un élève en programme, à qui le legacy passait l'élève du compte : la matière est alors
 * celle de sa dernière activité terminée (`lastActivitySubjectId`).
 */
export function regenerateScope(
    draft: ApiStudyMissionDraft | null | undefined,
    tutorStudent: ApiPrescriptionTutorStudent | null | undefined
): RegenerateScope {
    const scope = draft?.scope ?? {}
    const hasStudyProgram = tutorStudent?.has_study_program === true
    const subjectId = hasStudyProgram
        ? lastFinishedActivitySubjectId(tutorStudent?.activities)
        : scope.school_subject_id
    return {
        className: tutorStudent?.profile?.class ?? (hasStudyProgram ? null : scope.class) ?? null,
        // Les maths, quand rien ne dit la matière.
        subjectId: subjectId != null ? String(subjectId) : DEFAULT_SCHOOL_SUBJECT_ID,
        chapterId: scope.chapter_id != null ? String(scope.chapter_id) : null,
        hasStudyProgram,
    }
}

/** Les champs que le masteur a réellement touchés dans le formulaire de « Recommencer ». */
export type RegenerateTouched = { chapter: boolean; level: boolean; duration: boolean }

const toIntOrNull = (value: string | number | null | undefined): number | null => {
    if (value === null || value === undefined || value === "") return null
    const parsed = parseInt(String(value), 10)
    return Number.isNaN(parsed) ? null : parsed
}

/**
 * `buildRegeneratePayload.js` : ce que « Recommencer » envoie — seulement ce que le masteur a
 * choisi. Le formulaire s'ouvre pré-rempli ; renvoyer le pré-remplissage forcerait le serveur sur
 * une génération à neuf et une durée qu'il n'a pas demandées. Valider sans rien toucher envoie un
 * corps vide. En mode programme, le chapitre est celui du programme.
 */
export function regenerateOptions(args: {
    draft: UIActivityGenerationDraft
    touched: RegenerateTouched
    studyProgramChapterId: string | number | null
    useStudyProgram: boolean
}): Record<string, number | string> {
    const { draft, touched, studyProgramChapterId, useStudyProgram } = args
    const options: Record<string, number | string> = {}
    const put = (key: string, value: number | string | null) => {
        if (value !== null) options[key] = value
    }
    if (touched.chapter) put("chapter_id", toIntOrNull(useStudyProgram ? studyProgramChapterId : draft.chapterId))
    if (touched.level) put("level", toIntOrNull(draft.level))
    if (touched.duration) put("duration", toIntOrNull(draft.duration))
    put("instructions", draft.instructions.trim() || null)
    return options
}

/**
 * Un refus de « Donner à faire » ou de « Annuler » : la mission est-elle déjà ouverte par l'élève
 * (`mission_already_opened`, 409) ? La forme de l'erreur est celle de `apiRequest`.
 */
export function missionAlreadyOpened(error: unknown): boolean {
    const e = error as { code?: unknown; status?: unknown } | null | undefined
    return e?.code === "mission_already_opened" || e?.status === 409
}

/** Le retrait demandé a-t-il eu lieu ? Le serveur répond 200 même quand il n'a rien supprimé. */
export function removalApplied(data: unknown, activityId: string | number): boolean {
    const deleted = (data as { deleted_activity_ids?: unknown[] } | null | undefined)?.deleted_activity_ids ?? []
    return deleted.some((id) => String(id) === String(activityId))
}

/** La boîte d'une confirmation de retrait — les mots du `DialogM` du legacy, repliés compris. */
export function removeActivityDialog(studentName: string, activityTitle: string): UITutorPrescriptionDialog {
    return { kind: "remove_activity", studentName, activityTitle: activityTitle || "cette activité" }
}
